import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import {FormsService} from '@/services/forms.service'
import NotificationMessageService from '@/services/notificationmessage.service'
import type {JsonForm} from '@/entities/json-form'
import type {Form} from '@/entities/form'

const service = new FormsService()

const initialForm: JsonForm = {
  formConfig: {
    uniqueId: '',
    name: '',
    category: '',
    subcategoryId: 0,
    completedStep: 0,
    panelName: '',
    localizations: {},
  } as Form,
  steps: [],
  tabs: [],
  controls: [],
}

export const useFormStore = defineStore('form', () => {
  // --- state
  const form = ref<JsonForm>({...initialForm})
  const formTests = ref<any[]>([])
  const formsSubcategories = ref<any[]>([])
  const formsCategories = ref<any[]>([])

  const getFormTests = computed(() => {
    return formTests.value
  })

  // --- actions
  async function fetchForm([formId, testId]: [string, number]) {
    if (!formId) return
    try {
      const formResponse = await service.getForm(formId, testId)
      if (formResponse) {
        Object.assign(form.value, formResponse)
      }
    } catch {
      NotificationMessageService.showError(
        'Error while fetching form - id: {formId}', {formId}
      )
    }
  }

  async function fetchFormTests(formId: string) {
    if (!formId) return
    try {
      const res = await service.getFormTests(formId)
      if (res) {
        formTests.value = res
      }
    } catch {
      NotificationMessageService.showError(
        'Error while fetching form tests - form id: {formId}', {formId}
      )
    }
  }

  async function fetchFormsSubcategories() {
    try {
      const res = await service.getSubCategories()
      if (res) {
        formsSubcategories.value = res
      }
    } catch {
      NotificationMessageService.showError('Error while fetching forms subcategories.')
    }
  }

  async function fetchFormsCategories() {
    try {
      const res = await service.getFormsCategories()
      if (res) {
        formsCategories.value = res
      }
    } catch {
      NotificationMessageService.showError('Error while fetching forms categories.')
    }
  }

  function setForm(newForm: JsonForm) {
    Object.assign(form.value, newForm)
  }

  function resetState() {
    form.value = {...initialForm}
    formTests.value = []
    formsSubcategories.value = []
    formsCategories.value = []
  }

  function setStepValidity(stepOrder: number, isValid: boolean) {
    const step = form.value.steps.find(s => s.sortOrder === stepOrder)
    if (step) {
      step.valid = isValid
    }
  }

  return {

    // state
    form,
    formTests,
    formsSubcategories,
    formsCategories,

    // getters
    getFormTests,

    // actions
    fetchForm,
    fetchFormTests,
    fetchFormsSubcategories,
    fetchFormsCategories,
    setForm,
    resetState,
    setStepValidity
  }
})
